import { useLayoutEffect, useMemo, useRef } from "react";
import { LayoutDashboard, Settings, Truck, Bell, MapPin, Users, CreditCard, FileText, Wrench, Smartphone, BarChart3, UserCircle, IdCard, FileBarChart, Camera, Package, Gauge, Map, Activity, Terminal, CalendarClock, Route as RouteIcon } from "lucide-react";
import { version } from "../../../package.json";
import { NavLink } from "@/components/NavLink";
import { useLocation, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useResellerAccess } from "@/hooks/useResellerAccess";
import { useBranding } from "@/hooks/useBranding";

import {
  Sidebar,
  SidebarHeader,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarFooter,
  useSidebar,
} from "@/components/ui/sidebar";

const MAIN_TITLE_KEYS = [
  "nav.dashboard",
  "nav.liveMap",
  "nav.fleetVehicles",
  "nav.cameras",
  "nav.reports",
  "nav.performanceReport",
  "nav.activity",
  "nav.routes",
  "nav.maintenance",
] as const;

const RESELLER_TITLE_KEYS = [
  "nav.overview",
  "nav.customers",
  "nav.deviceStock",
  "nav.simCards",
  "nav.contracts",
  "nav.invoices",
  "nav.fieldServices",
  "nav.commands",
] as const;

const SECONDARY_TITLE_KEYS = [
  "nav.users",
  "nav.drivers",
  "nav.geofences",
  "nav.notifications",
  "nav.scheduledReports",
  "nav.preferences",
] as const;

let measureCanvas: HTMLCanvasElement | null = null;

function canvasTextWidth(text: string, font: string) {
  if (typeof document === "undefined") return text.length * 8;
  measureCanvas ??= document.createElement("canvas");
  const context = measureCanvas.getContext("2d");
  if (!context) return text.length * 8;
  context.font = font;
  return context.measureText(text).width;
}

/** Width for the first paint, before the sidebar DOM can be measured. */
export function useFittedSidebarWidth() {
  const { t, i18n } = useTranslation();
  const { isReseller } = useResellerAccess();
  const { title: brandTitle } = useBranding();

  return useMemo(() => {
    const family = typeof document === "undefined" ? "sans-serif" : getComputedStyle(document.body).fontFamily;
    const keys = [
      ...MAIN_TITLE_KEYS,
      ...(isReseller ? RESELLER_TITLE_KEYS : []),
      ...SECONDARY_TITLE_KEYS,
    ];
    const longest = Math.max(...keys.map((key) => canvasTextWidth(t(key), `400 14px ${family}`)));
    const brand = brandTitle || t("login.title");
    // Group padding, button padding, icon, and gap. Matches the sidebar menu classes.
    const menuWidth = longest + 56;
    const brandWidth = canvasTextWidth(brand, `600 14px ${family}`) + 80;
    const footerWidth = canvasTextWidth(`© 2025 ${brand}`, `400 12px ${family}`)
      + canvasTextWidth(`v${version}`, `400 12px ${family}`)
      + 40;
    return Math.ceil(Math.max(menuWidth, brandWidth, footerWidth) + 1);
  }, [t, i18n.language, isReseller, brandTitle]);
}

export function AppSidebar({ headerHeight, onContentWidth }: { headerHeight: number; onContentWidth?: (width: number) => void }) {
  const { t, i18n } = useTranslation();
  const { state } = useSidebar();
  const sidebarRef = useRef<HTMLDivElement>(null);
  const collapsed = state === "collapsed";
  const location = useLocation();
  const currentPath = location.pathname;
  const currentSearch = location.search;
  const { isReseller } = useResellerAccess();
  const { title: brandTitle, favicon: brandFavicon } = useBranding();

  const [searchParams] = useSearchParams();
  const deviceId = searchParams.get('deviceId');
  const reportsUrl = deviceId ? `/reports?deviceId=${deviceId}` : '/reports';

  const isActive = (path: string) => {
    const [itemPath, itemSearch = ''] = path.split('?');
    if (itemPath !== currentPath) return false;
    return itemSearch ? currentSearch === `?${itemSearch}` : !currentSearch;
  };

  const mainItems = [
    { titleKey: "nav.dashboard", url: "/", icon: LayoutDashboard },
    { titleKey: "nav.liveMap", url: "/live-map", icon: Map },
    { titleKey: "nav.fleetVehicles", url: "/devices", icon: Truck },
    { titleKey: "nav.cameras", url: "/cameras", icon: Camera },
    { titleKey: "nav.reports", url: reportsUrl, icon: FileBarChart },
    { titleKey: "nav.performanceReport", url: "/performance-report", icon: Gauge },
    { titleKey: "nav.activity", url: "/history/trips", icon: Activity },
    { titleKey: "nav.routes", url: "/routes", icon: RouteIcon },
    { titleKey: "nav.maintenance", url: "/maintenance", icon: CalendarClock },
  ];


  const resellerItems = [
    { titleKey: "nav.overview", url: "/reseller", icon: BarChart3 },
    { titleKey: "nav.customers", url: "/customers", icon: Users },
    { titleKey: "nav.deviceStock", url: "/device-stock", icon: Package },
    { titleKey: "nav.simCards", url: "/sim-cards", icon: Smartphone },
    { titleKey: "nav.contracts", url: "/contracts", icon: FileText },
    { titleKey: "nav.invoices", url: "/invoices", icon: CreditCard },
    { titleKey: "nav.fieldServices", url: "/field-services", icon: Wrench },
    { titleKey: "nav.commands", url: "/commands", icon: Terminal },
  ];

  const secondaryItems = [
    { titleKey: "nav.users", url: "/users", icon: UserCircle },
    { titleKey: "nav.drivers", url: "/drivers", icon: IdCard },
    { titleKey: "nav.geofences", url: "/geofences", icon: MapPin },
    { titleKey: "nav.notifications", url: "/notifications", icon: Bell },
    { titleKey: "nav.scheduledReports", url: "/settings?tab=scheduled", icon: CalendarClock },
    { titleKey: "nav.preferences", url: "/settings", icon: Settings },
  ];

  useLayoutEffect(() => {
    const root = sidebarRef.current;
    if (!root || collapsed || !onContentWidth) return;

    const textWidth = (el: HTMLElement) => {
      const source = getComputedStyle(el);
      const clone = el.cloneNode(true) as HTMLElement;
      clone.style.position = 'fixed';
      clone.style.left = '-9999px';
      clone.style.width = 'max-content';
      clone.style.maxWidth = 'none';
      clone.style.whiteSpace = 'nowrap';
      clone.style.overflow = 'visible';
      clone.style.font = source.font;
      clone.style.letterSpacing = source.letterSpacing;
      document.body.appendChild(clone);
      const width = clone.getBoundingClientRect().width;
      clone.remove();
      return width;
    };

    const measure = () => {
      const labels = root.querySelectorAll<HTMLElement>('[data-sidebar="menu-button"] span');
      let labelWidth = 0;
      labels.forEach((el) => {
        labelWidth = Math.max(labelWidth, textWidth(el));
      });
      if (labelWidth === 0) return;

      const button = labels[0].closest<HTMLElement>('[data-sidebar="menu-button"]');
      const group = button?.closest<HTMLElement>('[data-sidebar="group"]');
      const buttonStyle = button ? getComputedStyle(button) : null;
      const groupStyle = group ? getComputedStyle(group) : null;
      const icon = button?.querySelector('svg');
      const menuWidth = labelWidth
        + (buttonStyle ? parseFloat(buttonStyle.paddingLeft) + parseFloat(buttonStyle.paddingRight) : 0)
        + (buttonStyle ? parseFloat(buttonStyle.columnGap || buttonStyle.gap) : 0)
        + (icon ? icon.getBoundingClientRect().width : 0)
        + (groupStyle ? parseFloat(groupStyle.paddingLeft) + parseFloat(groupStyle.paddingRight) : 0);

      const headerLabel = root.querySelector<HTMLElement>('[data-sidebar="header"] span');
      const header = headerLabel?.parentElement;
      const headerStyle = header ? getComputedStyle(header) : null;
      const logo = header?.querySelector('img');
      const brandWidth = headerLabel
        ? textWidth(headerLabel)
          + (headerStyle ? parseFloat(headerStyle.paddingLeft) + parseFloat(headerStyle.paddingRight) : 0)
          + (headerStyle ? parseFloat(headerStyle.columnGap || headerStyle.gap) : 0)
          + (logo ? logo.getBoundingClientRect().width : 0)
        : 0;

      const footer = root.querySelector<HTMLElement>('[data-sidebar="footer"] p');
      const footerParts = footer ? [...footer.children] as HTMLElement[] : [];
      const footerStyle = footer ? getComputedStyle(footer) : null;
      const footerBoxStyle = footer?.parentElement ? getComputedStyle(footer.parentElement) : null;
      const footerWidth = footerParts.length
        ? footerParts.reduce((sum, el) => sum + textWidth(el), 0)
          + (footerStyle ? parseFloat(footerStyle.columnGap || footerStyle.gap) || 0 : 0) * (footerParts.length - 1)
          + (footerBoxStyle ? parseFloat(footerBoxStyle.paddingLeft) + parseFloat(footerBoxStyle.paddingRight) : 0)
        : 0;

      const panel = root.querySelector('[data-sidebar="sidebar"]')?.parentElement;
      const border = panel ? parseFloat(getComputedStyle(panel).borderRightWidth) || 0 : 0;
      onContentWidth(Math.ceil(Math.max(menuWidth, brandWidth, footerWidth) + border));
    };

    measure();
    const observer = new ResizeObserver(measure);
    root.querySelectorAll('[data-sidebar="menu-button"] span, [data-sidebar="header"] span, [data-sidebar="footer"] span').forEach((el) => observer.observe(el));
    const mutations = new MutationObserver(measure);
    mutations.observe(root, { childList: true, subtree: true });
    return () => {
      observer.disconnect();
      mutations.disconnect();
    };
  }, [collapsed, isReseller, i18n.language, brandTitle, onContentWidth]);

  return (
    <Sidebar ref={sidebarRef} collapsible="icon">
      <SidebarHeader
        style={{ height: headerHeight, minHeight: headerHeight }}
        className="flex-row items-center gap-3 border-b border-sidebar-border px-4 py-3 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-2"
      >
        <img
          src={brandFavicon || '/favicon.svg'}
          alt={brandTitle || t('login.title')}
          className="h-9 w-9 shrink-0"
        />
        {!collapsed && (
          <span className="truncate text-sm font-semibold">
            {brandTitle || t('login.title')}
          </span>
        )}
      </SidebarHeader>
      <SidebarContent className="pt-2">
        <SidebarGroup>
          <SidebarGroupLabel>{t("nav.fleet")}</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {mainItems.map((item) => (
                <SidebarMenuItem key={item.titleKey}>
                  <SidebarMenuButton
                    asChild
                    isActive={isActive(item.url)}
                    tooltip={t(item.titleKey)}
                  >
                    <NavLink
                      to={item.url}
                      end
                      className="flex items-center gap-2"
                    >
                      <item.icon className="h-4 w-4" />
                      <span>{t(item.titleKey)}</span>
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {isReseller && (
          <SidebarGroup>
            <SidebarGroupLabel>{t("nav.reseller")}</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {resellerItems.map((item) => (
                  <SidebarMenuItem key={item.titleKey}>
                    <SidebarMenuButton
                      asChild
                      isActive={isActive(item.url)}
                      tooltip={t(item.titleKey)}
                    >
                      <NavLink
                        to={item.url}
                        end
                        className="flex items-center gap-2"
                      >
                        <item.icon className="h-4 w-4" />
                        <span>{t(item.titleKey)}</span>
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        <SidebarGroup>
          <SidebarGroupLabel>{t("nav.settings")}</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {secondaryItems.map((item) => (
                <SidebarMenuItem key={item.titleKey}>
                  <SidebarMenuButton
                    asChild
                    isActive={isActive(item.url)}
                    tooltip={t(item.titleKey)}
                  >
                    <NavLink
                      to={item.url}
                      end
                      className="flex items-center gap-2"
                    >
                      <item.icon className="h-4 w-4" />
                      <span>{t(item.titleKey)}</span>
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="border-t border-sidebar-border p-4">
        {!collapsed && (
          <div className="text-xs text-muted-foreground">
            <p className="flex justify-between gap-2">
              <span className="truncate">© 2025 {brandTitle || t('login.title')}</span>
              <span className="shrink-0 opacity-50">v{version}</span>
            </p>

          </div>
        )}
      </SidebarFooter>
    </Sidebar>
  );
}
